import React, {
  SyntheticEvent,
  useRef,
  useState,
  MutableRefObject,
  useMemo,
  useEffect,
  CSSProperties,
} from 'react';
import { IterableObject, ReactRenderElement } from '../types/types';
import { isSimilarObject } from '../utils/object';

type Props = {
  disabled?: boolean;
  setRef?: MutableRefObject<HTMLFormElement | null>;
  children?: ReactRenderElement;
  style?: CSSProperties;
  onSubmit?: (evt: SyntheticEvent) => void;
};

type ChangedState<FS> = Partial<FS>;
export type FormStateRet<FS> = {
  formRef: MutableRefObject<HTMLFormElement | null>;
  formState: FS;
  formStateRef: MutableRefObject<FS>;
  changedState: ChangedState<FS>;
  handleChange: (evt: EventTarget | SyntheticEvent) => void;
  handleSubmit: (
    callback: (formState: FS, changedState: ChangedState<FS>) => void,
  ) => (evt?: SyntheticEvent) => void;
  resetState: (updatedInitialValue?: FS | (() => FS)) => void;
  setFormState: (state: FS | (() => FS)) => void;
  submit: () => void;
  updateState: (callback: UpdateStateCallback<FS>) => void;
  error: FormStateError;
  hasChanged: boolean;
  formKeyCount: IterableObject<string>;
  updateFormKeyCount: (...keys: string[]) => void;
};

type EventTarget = {
  target: {
    name: string;
    value: any;
  };
};

export type BasicFormEventTarget = EventTarget;

type FormStateError = {
  message?: string;
  [key: string]: any;
} | null;

type UpdateStateCallback<FS = any> = (state: FS) => FS;

export type FormOptions<FS> = {
  onStateUpdated?: (state: FS, changed: Partial<FS>) => void;
};

export function useForm<FS>(
  initialValue: FS | (() => FS) = {} as FS,
  options?: FormOptions<FS>,
): FormStateRet<FS> {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [originalState, setOriginalState] = useState<FS>(initialValue);
  const [formState, setFormState] = useState<FS>(initialValue);
  const [changedState, setChangedState] = useState<ChangedState<FS>>(
    {} as ChangedState<FS>,
  );
  const [error, setError] = useState<FormStateError>(null);

  const [formKeyCount, setFormKeyCount] = useState<{ [key: string]: string }>(
    {},
  );
  const formStateRef = useRef<FS>(formState);
  formStateRef.current = formState;

  const hasChanged = useMemo(
    () => !isSimilarObject(originalState, formState),
    [originalState, formState],
  );

  function handleChange(evt: EventTarget | SyntheticEvent) {
    const input = evt.target as HTMLInputElement;
    const name = input.name;
    const value = input.value;

    if (!name) throw new Error('Missing name on input field');

    setChangedState((state) => ({ ...state, [name]: value }));
    setFormState((state) => ({ ...state, [name]: value }));
  }

  function updateState(callback: UpdateStateCallback<FS>) {
    setChangedState((state) => callback(state as FS));
    setFormState((state) => callback(state as FS));
  }

  function handleSubmit(
    callback: (formState: FS, changedState: ChangedState<FS>) => void,
  ): (evt?: SyntheticEvent) => void {
    return function (evt?: SyntheticEvent) {
      evt?.preventDefault?.();
      try {
        setError(null);
        callback?.(formState, changedState);
      } catch (e) {
        setError({ message: (e as Error).message });
      }
    };
  }

  function updateFormKeyCount(...keys: string[]) {
    setFormKeyCount((state) => ({
      ...keys.reduce(
        (s, key) => {
          let v = (+(s[key] || '').replace(`${key}_`, '') || 0) + 1;
          s[key] = `${key}_${v}`;
          return s;
        },
        { ...state },
      ),
    }));
  }

  function resetState(updatedInitialValue?: FS | (() => FS)) {
    if (typeof updatedInitialValue !== 'undefined') {
      setFormState(updatedInitialValue);
      setOriginalState(updatedInitialValue);
    } else {
      setFormState(initialValue);
      setOriginalState(initialValue);
    }
    setChangedState({});
  }

  function submit() {
    formRef.current?.dispatchEvent(
      new Event('submit', { bubbles: true, cancelable: true }),
    );
  }

  useEffect(() => {
    options?.onStateUpdated?.(formState, changedState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, changedState]);

  return {
    formRef,
    formState,
    formStateRef,
    hasChanged,
    changedState,
    handleChange,
    handleSubmit,
    resetState,
    setFormState,
    submit,
    updateState,
    error,
    formKeyCount,
    updateFormKeyCount,
  };
}

const BasicForm = ({ setRef, children, onSubmit, style }: Props) => {
  return (
    <form
      ref={setRef}
      autoComplete='off'
      autoSave='off'
      autoCapitalize='off'
      autoCorrect='off'
      style={{ width: '100%', ...style }}
      onSubmit={onSubmit}
    >
      <fieldset
        style={{
          border: 0,
          margin: 0,
          padding: 0,

          display: 'block',
        }}
      >
        {children}
      </fieldset>
    </form>
  );
};
export default BasicForm;
